import { Stripe } from 'stripe';
import {
	ArrayMinSize,
	IsArray,
	IsBoolean,
	IsDateString,
	IsInt,
	IsNotEmpty,
	IsOptional,
	IsString,
	IsUUID,
	ValidateNested,
	MinLength,
	IsEmail,
	Length,
	ValidateIf,
} from 'class-validator';
import {
	CreateEventTicketDto,
	EventDto,
	EventTicketDto,
	ListWithTotals,
	UserDto,
	DiscountDto,
	UserToMembershipDto,
	MembershipTierDto,
	IntakeFormUserDataResults,
	TicketWithoutPaymentDetailsDto,
	UserToChallengeDto,
	BlastPlanDto,
} from '.';
import { Type, Transform } from 'class-transformer';
import { stringToBooleanNullable } from '../../types-transformers';
import { OrderStatusEnum } from '../../enums';
import { ChallengeDto } from './challenges.dto';

export interface PaymentDetailsDto {
	amount: number;
	appFee: number;
	baseAmount: number;
	stripeFee: number;
	fullFee: number;
	currency: string;
	buyerUserId: string;
	buyerUserIpAndAgent: string;
	eventOwnerId: string;
	ticketCount: number;
	ticketsBaseAmount: number;
	donationEvent: boolean;
	upgradedToTierId?: string;

	freeEvent?: boolean;
	discountIds?: string[];
	discountAmount?: number;
	skipEventSignUp?: boolean;
	intakeFormResults?: IntakeFormUserDataResults[];

	isManuallyAdded?: boolean;

	/** @deprecated */
	discountId?: string;
}

export interface CreatedPaymentIntentDto {
	paymentIntent: string;
	paymentIntentId: string;
	ephemeralKey: string;
	customer: string;
	publishableKey: string;
	off_session?: boolean;
}

export class GetEventPricesAndFeesRequestDto {
	@IsString()
	@IsUUID()
	eventId: string;

	@IsString()
	@IsDateString()
	eventInstanceDate: string;

	@IsOptional()
	@IsInt()
	donationAmount?: number | null;

	@IsOptional()
	@IsArray()
	@Type(() => CreateEventTicketDto)
	@ValidateNested({ each: true })
	tickets?: CreateEventTicketDto[];

	@IsOptional()
	@IsArray()
	@Type(() => ApplyPromocodeDto)
	@ValidateNested({ each: true })
	applyPromocodes?: ApplyPromocodeDto[];
}

export interface GetPricesAndFeesDto {
	donationEvent: boolean;
	basePrice: number;
	ticketsBasePrice: number;
	ticketCount: number;
	sweatPalsFee: number;
	stripeFee: number;
	fullPrice: number;
	fullFee: number;
	discountAmount: number;
	membershipId?: string;
	membershipDiscountAmount: number;
	discountIds?: string[];
}

export interface CreatePaymentIntentDto extends GetPricesAndFeesDto {
	intent: CreatedPaymentIntentDto | null;
	orderId?: string;
}

export interface CustomPaymentIntent extends Omit<Stripe.PaymentIntent, 'client_secret' | 'created'> {
	created: string;
}

export interface PaymentIntentMetadata {
	metadata: {
		membershipTierId?: string;
		challengeId?: string;
		blastPlanId?: string;
		forCommunityId?: string;
		eventId?: string;
		eventInstanceDate?: string;
		eventOwnerUserId: string;
		buyerUserId: string;
		discountIds?: string;
		tickets: string;
		upgradedToTierId?: string;
		skipEventSignUp: string;
		donationEvent: string;
		donationAmount?: string;
		add_funds?: 'true';
	};
}

export class CreatePaymentIntent {
	@IsOptional()
	@IsString()
	@IsUUID()
	userId?: string;

	@IsString()
	@IsUUID()
	eventId: string;

	@IsString()
	instanceDate: string;

	@IsOptional()
	@IsInt()
	donationAmount?: number | null;

	@IsOptional()
	@IsBoolean()
	skipEventSignUp?: boolean;

	@IsOptional()
	@IsArray()
	@ArrayMinSize(1)
	@Type(() => CreateEventTicketDto)
	@ValidateNested({ each: true })
	tickets?: CreateEventTicketDto[];

	@IsOptional()
	@IsArray()
	@Type(() => ApplyPromocodeDto)
	@ValidateNested({ each: true })
	applyPromocodes?: ApplyPromocodeDto[];

	@Transform(stringToBooleanNullable)
	public bypassDuplicatesCheck?: boolean;

	/** @deprecated */
	@IsOptional()
	@IsString()
	public discountId?: string;
}

export class OrderDto {
	id: string;
	hostId?: string;
	userId: string;
	membershipTierId?: string;
	challengeId?: string;
	blastPlanId?: string;
	forCommunityId?: string;
	eventId?: string;
	eventInstanceDate?: Date;
	upgradedTicketId?: string;
	oldTicketId?: string;
	initialEventInstanceDate?: Date;
	emailSequenceNumber: number;
	paymentIntentId: string;
	paidAt?: Date;
	refundTriggeredByUserId?: string;
	refundTriggeredAt?: Date;
	status?: OrderStatusEnum;
	paymentDetails?: PaymentDetailsDto;
	isTicketUsed?: boolean;
	createdAt?: Date;
	updatedAt?: Date;
	deletedAt?: Date;

	event?: EventDto;
	user?: UserDto;
	host?: UserDto;
	discounts?: DiscountDto[];
	tickets?: EventTicketDto[];
	userToMembership?: UserToMembershipDto;
	membershipTier?: MembershipTierDto;
	upgradedTicket?: EventTicketDto;
	oldTicket?: EventTicketDto;
	userToChallenge?: UserToChallengeDto;
	challenge?: ChallengeDto;
	blastPlan?: BlastPlanDto;
}

export class OrdersWithTotals {
	list: OrderDto[];
	totalAmount: number;
	thisWeekAmount: number;
	thisDayAmount: number;
	thisMonthAmount: number;
}

export interface PaymentIntentsWithTotals {
	list: Array<{
		intent: CustomPaymentIntent;
		paymentDetails: PaymentDetailsDto;
		user: UserDto;
		event: EventDto;
	}>;
	total: number;
	nextPageCursor: string;
	hasMore: boolean;
}

export class ApplyPromocodeDto {
	@IsString()
	@IsNotEmpty()
	code: string;

	@IsString()
	@IsNotEmpty()
	priceId: string;
}

export class GetPricesAndFeesRequestDtoV2 {
	@IsOptional()
	@IsString()
	@IsUUID()
	membershipTierId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	challengeId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	blastPlanId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	forCommunityId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	eventId?: string;

	@IsOptional()
	@IsInt()
	donationAmount?: number | null;

	@IsOptional()
	@IsString()
	@IsDateString()
	eventInstanceDate?: string;

	@IsOptional()
	@IsArray()
	@Type(() => CreateEventTicketDto)
	@ValidateNested({ each: true })
	tickets?: CreateEventTicketDto[];

	@IsOptional()
	@IsString()
	applyPromocode?: string;
}

export class GetPricesAndFeesToUpgradeTicketRequestDtoV2 {
	@IsString()
	@IsUUID()
	ticketId?: string;

	@IsString()
	@IsUUID()
	eventPriceId?: string;
}

export class CreatePaymentIntentV2 {
	@IsOptional()
	@IsString()
	@IsUUID()
	membershipTierId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	challengeId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	blastPlanId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	forCommunityId?: string;

	@IsOptional()
	@IsString()
	@IsUUID()
	eventId?: string;

	@IsOptional()
	@IsDateString()
	instanceDate?: string;

	@IsOptional()
	@IsInt()
	donationAmount?: number | null;

	@IsOptional()
	@IsBoolean()
	skipEventSignUp?: boolean;

	@IsOptional()
	@IsArray()
	@ArrayMinSize(1)
	@Type(() => CreateEventTicketDto)
	@ValidateNested({ each: true })
	tickets?: CreateEventTicketDto[];

	@IsOptional()
	@IsString()
	applyPromocode?: string;

	@Transform(stringToBooleanNullable)
	public bypassDuplicatesCheck?: boolean;

	@IsOptional()
	intakeFormResults?: IntakeFormUserDataResults | Array<IntakeFormUserDataResults>;
}

export class PublicCreatePaymentIntentV2 extends CreatePaymentIntentV2 {
	@MinLength(3)
	@IsNotEmpty()
	phone: string;

	@IsEmail()
	@IsNotEmpty()
	email: string;

	@IsString()
	@IsNotEmpty()
	@MinLength(3)
	fullName: string;

	// Use this OR signInUpToken to validate phone
	@ValidateIf((o) => !o.signInUpToken && !o.recaptchaTokenV2 && !o.recaptchaTokenV3 && !o.e2eToken, { message: 'Either code or recaptchaToken is required' })
	@Length(6, 6)
	code?: string;

	// Use this OR code to validate phone
	@ValidateIf((o) => !o.code && !o.recaptchaTokenV2 && !o.recaptchaTokenV3 && !o.e2eToken, { message: 'Either code or recaptchaToken is required' })
	signInUpToken?: string;

	@ValidateIf((o) => !o.code && !o.signInUpToken && !o.recaptchaTokenV3 && !o.e2eToken, { message: 'Either code or recaptchaToken is required' })
	@IsString()
	recaptchaTokenV2?: string;

	@ValidateIf((o) => !o.code && !o.signInUpToken && !o.recaptchaTokenV2 && !o.e2eToken, { message: 'Either code or recaptchaToken is required' })
	@IsString()
	recaptchaTokenV3?: string;

	@ValidateIf((o) => !o.code && !o.signInUpToken && !o.recaptchaTokenV2 && !o.recaptchaTokenV3, { message: 'Either code or recaptchaToken is required' })
	@IsString()
	e2eToken?: string;
}

export class OrderListWithTotals implements ListWithTotals<OrderDto> {
	list: OrderDto[];

	total: number;
	offset: number;
	limit: number;
}

export class OrdersForDashboardDto {
	totalAmount: number;
	thisDayAmount: number;
	thisWeekAmount: number;
	thisMonthAmount: number;
	orders: OrderListWithTotals;
}

export class OrderWithoutPaymentDetailsDto {
	id: string;
	hostId?: string;
	userId: string;
	eventId?: string;
	eventInstanceDate?: Date;
	initialEventInstanceDate?: Date;
	status?: OrderStatusEnum;
	isTicketUsed?: boolean;
	upgradedTicketId?: string;
	oldTicketId?: string;

	event?: EventDto;
	user?: UserDto;
	tickets?: TicketWithoutPaymentDetailsDto[];
	upgradedTicket?: TicketWithoutPaymentDetailsDto;
	oldTicket?: TicketWithoutPaymentDetailsDto;

	// for compatibility with OrderDto
	// do not use these fields
	createdAt?: Date;
	updatedAt?: Date;
	deletedAt?: Date;
	paymentIntentId?: string;
	paidAt?: Date;
	paymentDetails?: PaymentDetailsDto;
	discounts?: DiscountDto[];
	membershipTierId?: string;
	userToMembership?: UserToMembershipDto;
	membershipTier?: MembershipTierDto;
	challengeId?: string;
	userToChallenge?: UserToChallengeDto;
	challenge?: ChallengeDto;
}

export class GetAccountLinkDto {
	userId?: string;
	returnUrl: string;
	refreshUrl: string;
}
